// Writer único de UNA cuota de `plan_pagos_activos` (Fase 3 de "Futuros: reagendar", Commit 2,
// 2026-09-25). Espejo de `guardarFechaCuotaFactura.js` (Fase 2c/2e), pero para el eje CapEx: mueve
// SOLO `fecha_vencimiento` de la cuota puntual — nunca renumera `numero_cuota` ni toca las cuotas
// hermanas del mismo plan (editar el plan completo sigue siendo exclusivo del kebab → "Editar plan de
// pagos" → `PlanPagosActivoModal`, RPC `guardar_plan_pagos_activo`, que NO se toca).
//
// A diferencia de `guardarFechaCuotaFactura.js`, esta RPC (`reagendar_cuota_activo`, migración
// `20260924d_rpc_reagendar_cuota_activo.sql`, YA APLICADA en producción — Commit 1 de esta fase) toma
// SOLO 3 parámetros (`p_cuota_id`, `p_nueva_fecha`, `p_pais`) — no hay `p_tabla`: `plan_pagos_activos`
// es una tabla única, no dos como `plan_cobros_facturas`/`plan_pagos_facturas`, así que no existe el
// cross-check `cuotaTablaOrigen` de esa Fase 2b (no hay tabla que verificar).
//
// Este writer se reduce a:
//
//   0. Validar los parámetros básicos en JS + `pais` ∈ {"Chile","Perú"} (10.53) ANTES de la red — mismo
//      criterio de lista blanca EXACTA que `guardarFechaCuotaFactura.js` (Fase 2e): la RPC igual lo
//      rechazaría del lado de la base (`RAISE EXCEPTION 'pais desconocido "%"'`), pero se prefiere
//      frenar acá, sin gastar una llamada de red (10.177: rechazo explícito, nunca un valor que viaja
//      "a ver si pasa").
//
//   1. Llamar `client.rpc("reagendar_cuota_activo", { p_cuota_id, p_nueva_fecha, p_pais })`. TODAS las
//      validaciones reales (cuota/activo existen, evento vigente en `pagos_capex` — parcial incluido,
//      piso de fecha por huso del país resuelto SERVER-SIDE, día hábil) viven DENTRO de la RPC, dentro
//      de la MISMA transacción que el `SELECT ... FOR UPDATE` del activo padre + la cuota (ver la
//      migración) — 10.1: no se duplican acá "por las dudas".
//
//   2. Si la RPC devuelve `{error}`: el mensaje real de Postgres llega con el prefijo
//      `"reagendar_cuota_activo: "` (ver la RPC, RAISE EXCEPTION) — se limpia ese prefijo para que el
//      toast que ve Ignacio sea legible. Un error SIN ese prefijo (caída de red, 42501/42883, cualquier
//      error inesperado de Postgres) se propaga TAL CUAL — nunca se esconde detrás de un mensaje
//      genérico (10.177). Un `{error}` cuyo `.message` NO es un string no vacío (edge case real de
//      ciertos errores de red/Postgres) NUNCA se trata como si no hubiera error ni devuelve un mensaje
//      vacío/confuso — cae a `"Error sin mensaje de la RPC (code ${error.code ?? "desconocido"})"`
//      (mismo patrón que la Fase 2d de `guardarFechaCuotaFactura.js`). Un `client.rpc` que RECHAZA la
//      promesa entera (excepción JS antes de llegar a un `{data,error}` normal de PostgREST — timeout,
//      red caída) tampoco revienta este writer sin control: se captura y se devuelve con el MISMO
//      contrato `{error:string}`, nunca una excepción no manejada.
//
//   3. Si no hay error: la RPC responde `jsonb_build_object('cuota', ..., 'activo_id', ...,
//      'fecha_planificada', ...)` (paso 10 de la migración) — este writer devuelve ese shape completo
//      (`cuota`, `activo_id`, `fecha_planificada`) además del contrato `{success:true, cuota_id,
//      nuevaFecha}` que ya usa `guardarFechaCuotaFactura.js`, para que el caller (`reagendarEntryFuturo`,
//      case "capex" con `tiene_plan`) arme el patch de AMBOS ejes (la cuota Y, si cambió, la
//      `fecha_planificada` del activo — 10.234, sin una query extra) sin inventar nada del lado del
//      frontend. `data:null` con `error:null` (edge case raro pero posible) se trata como ÉXITO con
//      `cuota:null`/`activo_id:null`/`fecha_planificada:null` — `{error:null}` es la señal autoritativa
//      de "no hubo error" en el contrato de `client.rpc`; no se inventa acá un tercer estado "ambiguo"
//      (mismo criterio que `guardarFechaCuotaFactura.js`).
//
// El predicado de pantalla `esFechaReagendable` (case "capex", rama `f.tiene_plan`) NO CAMBIA de
// responsabilidad — sigue siendo el gate de UI que decide qué fila es CLICKEABLE (cuota_id numérico +
// `!cuota_tiene_evento_vigente` + `ctx.planesCargados === true`); la RPC es la garantía real detrás de
// ese click, no el criterio de qué mostrar.
//
// CAJA PURA (10.2): este writer NUNCA toca `pagos_capex`/`aplicaciones_saldo`/MBF/`movimientos_caja` —
// CERO `.from()` en este archivo, todo pasa por la RPC (igual que `guardarFechaCuotaFactura.js`).
import { client } from "./supabase";

// Lista BLANCA de países soportados (10.53) — mismo criterio que `guardarFechaCuotaFactura.js`: la RPC
// usa `p_pais` para resolver "hoy" server-side por huso; se rechaza cualquier otro valor ANTES de
// llamar a la RPC (que igual lo rechazaría, pero se prefiere frenar acá — 10.177).
const PAISES_SOPORTADOS = new Set(["Chile", "Perú"]);

// Prefijo que la RPC antepone a TODOS sus mensajes (`RAISE EXCEPTION 'reagendar_cuota_activo: ...'`,
// ver supabase/migrations/20260924d_rpc_reagendar_cuota_activo.sql) — se limpia acá, nunca en la RPC
// (10.177: acá se limpia para legibilidad de UI, no se esconde el motivo real).
const PREFIJO_ERROR_RPC = "reagendar_cuota_activo: ";

/**
 * @param {Object} params
 * @param {number} params.cuota_id - `plan_pagos_activos.id`.
 * @param {string} params.nuevaFecha - "YYYY-MM-DD"
 * @param {string} params.pais - "Chile" | "Perú" EXACTO (10.53) — viaja tal cual como `p_pais` a la
 *   RPC, que resuelve "hoy" ELLA MISMA server-side por huso de país; cualquier otro valor se rechaza
 *   acá mismo, antes de llamar a la RPC.
 * @returns {Promise<{error:string}|{success:true, cuota_id:number, nuevaFecha:string, cuota:Object,
 *   activo_id:number, fecha_planificada:string|null}>}
 */
export const guardarFechaCuotaActivo = async ({ cuota_id, nuevaFecha, pais }) => {
  if (cuota_id == null) return { error: "guardarFechaCuotaActivo: falta cuota_id." };
  if (!nuevaFecha) return { error: "Seleccioná una fecha." };
  // `pais` viaja tal cual como `p_pais` a la RPC, que lo usa para resolver "hoy" server-side por huso
  // (10.53) — se valida acá, ANTES de la red, con el mismo criterio de lista blanca que
  // `guardarFechaCuotaFactura.js`.
  if (!PAISES_SOPORTADOS.has(pais)) {
    return { error: `guardarFechaCuotaActivo: país no soportado "${pais}".` };
  }

  let data;
  let error;
  try {
    ({ data, error } = await client.rpc("reagendar_cuota_activo", {
      p_cuota_id: cuota_id,
      p_nueva_fecha: nuevaFecha,
      p_pais: pais,
    }));
  } catch (err) {
    // `client.rpc` RECHAZANDO la promesa (excepción JS antes de un {data,error} normal — red caída,
    // timeout) nunca revienta este writer sin control (10.177) — se captura y se devuelve con el MISMO
    // contrato {error:string} del resto de esta función.
    return { error: err?.message || "Error de red al llamar reagendar_cuota_activo." };
  }
  if (error) {
    // Un {error} SIN .message (o con .message vacío) NUNCA se trata como éxito ni devuelve un mensaje
    // vacío/confuso — nunca un falso éxito (10.177).
    let mensaje;
    if (typeof error.message === "string" && error.message.length > 0) {
      mensaje = error.message.startsWith(PREFIJO_ERROR_RPC)
        ? error.message.slice(PREFIJO_ERROR_RPC.length)
        : error.message;
    } else {
      mensaje = `Error sin mensaje de la RPC (code ${error.code ?? "desconocido"})`;
    }
    return { error: mensaje };
  }
  return {
    success: true,
    cuota_id,
    nuevaFecha,
    cuota: data?.cuota ?? null,
    activo_id: data?.activo_id ?? null,
    fecha_planificada: data?.fecha_planificada ?? null,
  };
};
